/** Building Permits — v1 permits-roi.js permitsPanel/permitsAiFind, same
 * contract: permits ride on the lead (stamped by the county scraper or the
 * AI web search), rendered with v1's tolerant field accessors; the AI search
 * POSTs /api/permits/ai-find {address, county, lead_id, org_id} and the
 * server stamps what it finds onto the lead row (same class of enrichment as
 * comps/estimate — Phase A safe, nothing live-fires). */
import { backendFetch, type BackendOptions } from "../../lib/backend";

export interface PermitRec {
  num: string;
  type: string;
  work: string;
  status: string;
  date: string;
  desc: string;
  link: string | null;
  ai: boolean;
  website: string | null;
  /** county rows carry the job amount when the portal lists it ("$63,488") */
  amount?: string;
}

const isObj = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === "object" && !Array.isArray(v);

/** v1's G(): first non-empty of a key chain. */
const G = (o: Record<string, unknown>, keys: string[]): string => {
  for (const k of keys) {
    const v = o[k];
    if (v !== null && v !== undefined && v !== "") return String(v);
  }
  return "";
};

/** The AI often reports a bare domain ("realtor.com"); rendered as an href
 * that's a RELATIVE link into our own app (2026-08-13, 2586 Redwood Cir:
 * "permit site →" pointed at etopropertygroupcrm.com/realtor.com). Give
 * scheme-less values an https:// so they leave the app. */
const absUrl = (u: string): string | null => {
  const s = (u || "").trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) return s;
  if (/^[\w.-]+\.[a-z]{2,}([/?#]|$)/i.test(s)) return "https://" + s;
  return null; // not URL-shaped ("the county portal") — show as text elsewhere
};

export function narrowPermit(raw: unknown): PermitRec | null {
  if (!isObj(raw)) return null;
  const num = G(raw, ["permit_number", "number", "permitNumber", "record_id", "recordId", "id"]);
  const type = G(raw, ["type", "permit_type", "record_type"]);
  const status = G(raw, ["status", "permit_status"]);
  const desc = G(raw, ["description", "work_description", "scope"]);
  if (!num && !type && !status && !desc) return null;
  return {
    num,
    type,
    work: G(raw, ["work", "work_type", "work_class"]),
    status,
    date: G(raw, ["date", "issue_date", "issued_date", "issued", "open_date", "file_date", "applied_date", "application_date", "updated", "status_date"]),
    desc,
    link: absUrl(G(raw, ["link", "url", "record_url"])),
    ai: raw["source"] === "ai",
    website: absUrl(G(raw, ["website"])),
    amount: G(raw, ["amount", "job_value", "valuation", "estimated_cost"]),
  };
}

export const narrowPermits = (raw: unknown): PermitRec[] =>
  Array.isArray(raw) ? raw.map(narrowPermit).filter((p): p is PermitRec => p !== null) : [];

/** v1's status hue rules, verbatim. */
export function permitHue(status: string): "done" | "open" | "dead" | "unknown" {
  if (/final|clos|complet|issued|c\.?o\.?/i.test(status)) return "done";
  if (/open|active|progress|review|pend/i.test(status)) return "open";
  if (/expire|void|cancel|withdraw/i.test(status)) return "dead";
  return "unknown";
}

// ---- county resolution (v1 FL_CITY_CTY, verbatim) ----------------------------

const FL_CITY_CTY: Record<string, string> = {
  "new port richey": "Pasco", "port richey": "Pasco", "hudson": "Pasco", "holiday": "Pasco", "wesley chapel": "Pasco", "land o lakes": "Pasco", "zephyrhills": "Pasco", "dade city": "Pasco", "trinity": "Pasco",
  "orlando": "Orange", "winter park": "Orange", "apopka": "Orange", "ocoee": "Orange", "winter garden": "Orange",
  "kissimmee": "Osceola", "st cloud": "Osceola", "saint cloud": "Osceola", "poinciana": "Osceola",
  "sanford": "Seminole", "altamonte springs": "Seminole", "casselberry": "Seminole", "longwood": "Seminole", "oviedo": "Seminole", "winter springs": "Seminole", "lake mary": "Seminole",
  "deltona": "Volusia", "daytona beach": "Volusia", "deland": "Volusia", "ormond beach": "Volusia", "port orange": "Volusia", "new smyrna beach": "Volusia", "edgewater": "Volusia", "debary": "Volusia", "orange city": "Volusia",
  "tampa": "Hillsborough", "brandon": "Hillsborough", "riverview": "Hillsborough", "plant city": "Hillsborough", "valrico": "Hillsborough", "ruskin": "Hillsborough", "seffner": "Hillsborough",
  "clearwater": "Pinellas", "st petersburg": "Pinellas", "saint petersburg": "Pinellas", "largo": "Pinellas", "pinellas park": "Pinellas", "palm harbor": "Pinellas", "tarpon springs": "Pinellas", "dunedin": "Pinellas",
  "lakeland": "Polk", "winter haven": "Polk", "davenport": "Polk", "haines city": "Polk", "auburndale": "Polk", "bartow": "Polk", "lake wales": "Polk",
  "ocala": "Marion",
  "the villages": "Sumter", "wildwood": "Sumter", "bushnell": "Sumter",
  "clermont": "Lake", "leesburg": "Lake", "eustis": "Lake", "tavares": "Lake", "mount dora": "Lake", "groveland": "Lake",
  "melbourne": "Brevard", "palm bay": "Brevard", "titusville": "Brevard", "cocoa": "Brevard", "rockledge": "Brevard", "merritt island": "Brevard",
  "spring hill": "Hernando", "brooksville": "Hernando",
  "gainesville": "Alachua",
  "palm coast": "Flagler",
  "bradenton": "Manatee",
  "sarasota": "Sarasota", "venice": "Sarasota", "north port": "Sarasota",
  "port st lucie": "St. Lucie", "fort pierce": "St. Lucie",
  "vero beach": "Indian River", "sebastian": "Indian River",
};

/** v1 ctyOf: the lead's county, else city→county from the ", City, FL" part. */
export function countyOf(county: string | null, address: string): string {
  if (county) return county.replace(/\s*county\s*$/i, "");
  const m = String(address || "").match(/,\s*([A-Za-z .']+?)\s*,?\s*FL\b/i);
  const city = (m?.[1] ?? "").trim().toLowerCase().replace(/\./g, "");
  return FL_CITY_CTY[city] ?? "";
}

/** v1's portal link: stored permit URL, else a county-scoped Google search. */
export function permitPortalUrl(permitUrl: string | null, county: string, address: string): string {
  if (permitUrl) return permitUrl;
  return "https://www.google.com/search?q=" + encodeURIComponent((county ? county + " County FL " : "") + "building permit search " + (address || ""));
}

// ---- the AI search -----------------------------------------------------------

export interface AiPermitResult {
  ok: boolean;
  permits: PermitRec[];
  count: number;
  website: string | null;
  detail: string | null;
  /** county-first gate (Tom 2026-08-13): has-data = county rows returned, AI
   * skipped; down = county path broken (alert set); ok-empty = county path
   * healthy but no rows, AI ran as backup. */
  countyStatus: "has-data" | "down" | "ok-empty" | "unknown";
  skipped: boolean;
  alert: string | null;
}

export async function runAiPermitSearch(
  token: string | null,
  address: string,
  county: string,
  leadId: string | null,
  orgId: string,
  opts?: Pick<BackendOptions, "signal" | "onWake"> & { force?: boolean },
): Promise<AiPermitResult> {
  try {
    const j = await backendFetch<Record<string, unknown>>(token, "/api/permits/ai-find", {
      method: "POST",
      body: { address, county, lead_id: leadId, org_id: orgId, force: opts?.force === true },
      // latest reasoning model + web search can run 1-3 min
      timeoutMs: 240000,
      ...(opts?.signal ? { signal: opts.signal } : {}),
      ...(opts?.onWake ? { onWake: opts.onWake } : {}),
    });
    const permits = narrowPermits(j?.["permits"]);
    const cs = j?.["county_status"];
    return {
      ok: true,
      permits,
      count: typeof j?.["count"] === "number" ? (j["count"] as number) : permits.length,
      website: typeof j?.["website"] === "string" ? (j["website"] as string) : null,
      detail: null,
      countyStatus: cs === "has-data" || cs === "down" || cs === "ok-empty" ? cs : "unknown",
      skipped: j?.["skipped"] === true,
      alert: typeof j?.["alert"] === "string" && j["alert"] ? (j["alert"] as string) : null,
    };
  } catch (e) {
    return { ok: false, permits: [], count: 0, website: null, detail: e instanceof Error ? e.message : "Could not reach the backend", countyStatus: "unknown", skipped: false, alert: null };
  }
}

/** v1 merge rule: new permits join, deduped by permit number. */
export function mergePermits(existing: PermitRec[], found: PermitRec[]): PermitRec[] {
  return [...existing, ...found.filter((np) => !existing.some((op) => op.num === np.num))];
}

export const PERMIT_MSG = {
  emptyChecked: "No permits on record from the county scraper — try the AI search as a second opinion.",
  emptyNew: "The county permit scraper has not reached this lead yet — you can run the AI search now.",
  aiNone: (site: string | null) =>
    "AI found no permit records for this address" + (site ? " — checked " + site.replace(/^https?:\/\//, "").slice(0, 40) : ""),
  aiFound: (n: number) => `${n} permit${n > 1 ? "s" : ""} found ✓`,
  aiFailed: (d: string) => "AI search failed: " + d.slice(0, 140),
};
